
import {
    Box,
    Button,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect } from 'react';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import { queueGuidedTourLaunch } from '../../guided-tour/guidedTourStorage';
import AddBrandImage from '../../services/AddBrandImage';
import './Biodetails.scss';

export const maxFileSize = 10 * 1024 * 1024;

const STEPS = ['Account Type', 'Business Details', 'Address & Links'];

export default function Biodetails(props) {
    const [formData, setFormData] = React.useState({
        firstName: '',
        lastName: '',
        companyName: '',
        role: 'ROLE_EMPLOYEE',
        userName: '',
        email: '',
        userId: '',
        reportingManagerId: '',
        company: {},
    });
    const [selectedRole, setSelectedRole] = React.useState('ROLE_CMANAGER');
    const [imageData, setImageData] = React.useState(null);
    const [activeStep, setActiveStep] = React.useState(0);
    const authContext = useAuth();
    const [loading, setLoading] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const draftKey = React.useMemo(() => {
        const userId = props.user?.username || props.user?.signInDetails?.loginId || 'anonymous';
        return `biodetails-draft-${userId}`;
    }, [props.user]);

    const isNewU =
        authContext.role === 'ROLE_NEWU' ||
        authContext.additionalUserInfo?.role === 'ROLE_NEWU' ||
        !authContext.additionalUserInfo?.role;

    const mergeDraftWithBase = (base, draft) => {
        const result = { ...base };
        Object.keys(draft || {}).forEach((key) => {
            const value = draft[key];
            if (value !== undefined && value !== null && value !== '') {
                result[key] = value;
            }
        });
        return result;
    };

    useEffect(() => {
        const userInfo = (props.additionalUserInfo && Object.keys(props.additionalUserInfo).length > 0)
            ? props.additionalUserInfo
            : (authContext.additionalUserInfo || {});
        const company = userInfo.company || {};
        const firstCompanyLocation = Array.isArray(company.locations) ? (company.locations[0] || {}) : {};
        const initialRole = userInfo.role && userInfo.role !== 'ROLE_NEWU' ? userInfo.role : 'ROLE_CMANAGER';
        const signedInEmail =
            props.user?.signInDetails?.loginId ||
            props.user?.attributes?.email ||
            userInfo.email ||
            '';
        const managerEmail = userInfo.reportingManagerId || '';
        let draft = null;

        try {
            const raw = window.sessionStorage.getItem(draftKey);
            draft = raw ? JSON.parse(raw) : null;
        } catch (e) {
            draft = null;
        }

        const baseFormData = {
            firstName: userInfo.firstName || '',
            lastName: userInfo.lastName || '',
            phone: userInfo.phone || userInfo.phoneNumber || '',
            companyName: userInfo.companyName || company.companyName || '',
            companyABN: userInfo.companyABN || company.companyABN || '',
            companyAddress: company.companyAddress || '',
            brandImageUrl: userInfo.brandImageUrl || company.brandImageUrl || '',
            companyId: userInfo.companyId || company.companyId || company.id || '',
            reportingManagerId: managerEmail,
            userId: userInfo.userId || '',
            streetNo: firstCompanyLocation.streetNo || userInfo.streetNo || '',
            streetName: firstCompanyLocation.streetName || userInfo.streetName || '',
            suburb: firstCompanyLocation.suburb || userInfo.suburb || '',
            city: firstCompanyLocation.city || userInfo.city || '',
            country: firstCompanyLocation.country || userInfo.country || '',
            role: initialRole,
            userName: props.user?.username || signedInEmail || '',
            email: signedInEmail,
            company: {
                ...company,
                companyName: userInfo.companyName || company.companyName || '',
                companyABN: userInfo.companyABN || company.companyABN || '',
                companyAddress: company.companyAddress || '',
                brandImageUrl: userInfo.brandImageUrl || company.brandImageUrl || '',
                companyId: userInfo.companyId || company.companyId || '',
            },
        };
        const draftFormData = draft?.formData || {};
        const mergedRole = draftFormData.role || draft?.selectedRole || initialRole;

        setSelectedRole(mergedRole);
        if (Number.isInteger(draft?.activeStep) && draft.activeStep >= 0 && draft.activeStep < STEPS.length) {
            setActiveStep(draft.activeStep);
        }
        setFormData((prev) => ({
            ...prev,
            ...baseFormData,
            ...mergeDraftWithBase(baseFormData, draftFormData),
            email: signedInEmail,
            reportingManagerId: draftFormData.reportingManagerId || managerEmail,
            role: mergedRole,
            company: {
                ...(baseFormData.company || {}),
                ...(draftFormData.company || {}),
            },
        }));
    }, [props.additionalUserInfo, props.user, draftKey, authContext.additionalUserInfo]);

    useEffect(() => {
        try {
            window.sessionStorage.setItem(draftKey, JSON.stringify({
                formData,
                selectedRole,
                activeStep,
            }));
        } catch (e) {
            // Ignore storage errors for private mode or quota limits
        }
    }, [formData, selectedRole, activeStep, draftKey]);

    const setRoleChoice = (role) => {
        setSelectedRole(role);
        setFormData((prev) => ({ ...prev, role }));
    };

    const addCompanyDetails = (value, field) => {
        setFormData((prev) => {
            const company = { ...(prev.company || {}), [field]: value };
            const topLevel = {};
            if (field === 'companyName') topLevel.companyName = value;
            if (field === 'companyABN') topLevel.companyABN = value;
            if (field === 'companyAddress') topLevel.companyAddress = value;
            return { ...prev, ...topLevel, company };
        });
    };

    const addAddressDetails = (value, field) => {
        setFormData((prev) => {
            const existingLocations = Array.isArray(prev.company?.locations) ? prev.company.locations : [];
            const firstLocation = existingLocations[0] || {};
            const updatedLocations = [
                {
                    ...firstLocation,
                    [field]: value,
                },
                ...existingLocations.slice(1),
            ];

            return {
                ...prev,
                [field]: value,
                company: {
                    ...(prev.company || {}),
                    locations: updatedLocations,
                },
            };
        });
    };

    const pickImage = (event) => {
        const file = event?.target?.files?.[0];
        if (!file) return;
        if (file.size > maxFileSize) {
            alert('The file size is too large');
            return;
        }
        const localUrl = URL.createObjectURL(file);
        setImageData({ file, localUrl, fileName: file.name, type: file.type });
    };

    const save = async () => {
        try {
            setLoading(true);
            const effectiveRole = isNewU ? selectedRole : (formData.role || selectedRole);

            const existingLocations = Array.isArray(formData.company?.locations) ? formData.company.locations : [];
            const firstLocation = existingLocations[0] || {};
            const companyLocations = [
                {
                    ...firstLocation,
                    streetNo: formData.streetNo || firstLocation.streetNo || '',
                    streetName: formData.streetName || firstLocation.streetName || '',
                    suburb: formData.suburb || firstLocation.suburb || '',
                    city: formData.city || firstLocation.city || '',
                    country: formData.country || firstLocation.country || '',
                },
                ...existingLocations.slice(1),
            ];

            const payload = {
                ...formData,
                role: effectiveRole,
                userName: formData.userName || formData.email,
                email: formData.email || props.user?.signInDetails?.loginId,
                company: {
                    ...(formData.company || {}),
                    companyName: formData.company?.companyName || formData.companyName,
                    companyABN: formData.company?.companyABN || formData.companyABN,
                    companyAddress: formData.company?.companyAddress || formData.companyAddress,
                    brandImageUrl: formData.company?.brandImageUrl || formData.brandImageUrl,
                    companyId: formData.company?.companyId || formData.companyId,
                    locations: companyLocations,
                },
            };

            if (imageData?.file) {
                const uploadForm = new FormData();
                uploadForm.append('file', imageData.file);
                try {
                    const brandUpload = await AddBrandImage(uploadForm, authContext.token);
                    const brandImageUrl = brandUpload?.brandImageUrl || brandUpload?.data?.brandImageUrl || '';
                    const brandImageKeyName = brandUpload?.brandImageKeyName || brandUpload?.data?.brandImageKeyName || '';
                    if (brandImageUrl) {
                        payload.brandImageUrl = brandImageUrl;
                        payload.brandImageKeyName = brandImageKeyName;
                        payload.company = {
                            ...(payload.company || {}),
                            brandImageUrl,
                            brandImageKeyName,
                        };
                    }
                } catch (uploadError) {
                    console.error('Error uploading brand image:', uploadError);
                }
            }

            const response = await axios.post(`${baseUrl_SPB}/api/v1/user/userbiosave`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + authContext.token,
                },
            });

            const userData = response.data;
            props.setAdditionalUserInfo && props.setAdditionalUserInfo(userData);
            props.setRole && props.setRole(userData.role);
            setFormData((prev) => ({ ...prev, ...userData }));
            setSaved(true);
            window.sessionStorage.removeItem(draftKey);
            const redirectPath = window.sessionStorage.getItem('postBiodetailsRedirect');
            if (redirectPath) {
                window.sessionStorage.removeItem('postBiodetailsRedirect');
            }
            queueGuidedTourLaunch(props.user?.username || props.user?.signInDetails?.loginId || formData.email || 'anonymous');
            // Navigate to root after a brief moment — the fresh role load will route correctly
            setTimeout(() => { window.location.href = redirectPath || '/my-profile'; }, 1500);
        } catch (error) {
            console.error('Error saving biodetails:', error);
        } finally {
            setLoading(false);
        }
    };

    const currentRole = authContext.role || authContext.additionalUserInfo?.role || '';
    const canEditCompanyDetails = currentRole === 'ROLE_NEWU' || currentRole === 'ROLE_CMANAGER';
    const canEditAddressDetails = currentRole === 'ROLE_NEWU' || currentRole === 'ROLE_CMANAGER';

    const renderStep = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box className="bio-step">
                        <Typography variant="h6" className="bio-step-title">Personal Information</Typography>
                        {isNewU && (
                            <Box className="bio-role-chooser">
                                <Typography variant="body2" className="labelText">Choose how you want to use the platform:</Typography>
                                <Box className="bio-role-buttons">
                                    <button
                                        className={`bio-role-btn${selectedRole === 'ROLE_FWORKER' ? ' active' : ''}`}
                                        onClick={() => setRoleChoice('ROLE_FWORKER')}
                                    >
                                        <span className="bio-role-icon">🧑‍💼</span>
                                        <span className="bio-role-label">Independent / Flexible Worker</span>
                                        <span className="bio-role-desc">I work independently or for multiple businesses</span>
                                    </button>
                                    <button
                                        className={`bio-role-btn${selectedRole === 'ROLE_CMANAGER' ? ' active' : ''}`}
                                        onClick={() => setRoleChoice('ROLE_CMANAGER')}
                                    >
                                        <span className="bio-role-icon">🏢</span>
                                        <span className="bio-role-label">Business Owner / Manager</span>
                                        <span className="bio-role-desc">I manage a team or own a business</span>
                                    </button>
                                </Box>
                            </Box>
                        )}
                        <TextField className="textField" label="First name" variant="outlined" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
                        <TextField className="textField" label="Last name" variant="outlined" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
                        <TextField disabled className="textField" label="Email address" variant="outlined" value={formData.email} />
                        <TextField className="textField" label="Mobile" variant="outlined" value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                    </Box>
                );
            case 1:
                return (
                    <Box className="bio-step">
                        <Typography variant="h6" className="bio-step-title">Business Details</Typography>
                        <Typography variant="body2" className="labelText">
                            {selectedRole === 'ROLE_FWORKER'
                                ? 'Independent workers should operate under a personal ABN.'
                                : 'Enter your business information below.'}
                        </Typography>
                        <TextField
                            disabled={!canEditCompanyDetails}
                            className="textField"
                            label="Business name"
                            variant="outlined"
                            value={formData.company?.companyName || formData.companyName || ''}
                            onChange={(e) => addCompanyDetails(e.target.value, 'companyName')}
                        />
                        <TextField
                            disabled={!canEditCompanyDetails}
                            className="textField"
                            label="Business ABN"
                            variant="outlined"
                            value={formData.company?.companyABN || formData.companyABN || ''}
                            onChange={(e) => addCompanyDetails(e.target.value, 'companyABN')}
                        />
                        {/*<TextField
                            disabled={!canEditCompanyDetails}
                            className="textField"
                            label="Business address"
                            variant="outlined"
                            multiline
                            rows={3}
                            value={formData.company?.companyAddress || formData.companyAddress || ''}
                            onChange={(e) => addCompanyDetails(e.target.value, 'companyAddress')}
                        />*/}
                        <Box className="bio-image-section">
                            <Typography variant="body2" className="labelText">Brand / Logo image</Typography>
                            {(imageData?.localUrl || formData.brandImageUrl) && (
                                <img
                                    src={imageData?.localUrl || formData.brandImageUrl}
                                    alt="Brand"
                                    className="bio-brand-preview"
                                />
                            )}
                            <Button variant="outlined" component="label" size="small" disabled={!canEditCompanyDetails}>
                                {imageData ? 'Change image' : 'Upload image'}
                                <input hidden type="file" accept="image/*" onChange={pickImage} />
                            </Button>
                            {imageData && <Typography variant="caption" sx={{ ml: 1 }}>{imageData.fileName}</Typography>}
                        </Box>
                    </Box>
                );
            case 2:
                return (
                    <Box className="bio-step">
                        <Typography variant="h6" className="bio-step-title">Address & Connections</Typography>
                        <Typography variant="body2" className="labelText">Please provide an address:</Typography>
                        <Box className="bio-address-row">
                            <TextField
                                className="textField bio-field-sm"
                                label="Street no"
                                variant="outlined"
                                disabled={!canEditAddressDetails}
                                value={formData.streetNo || ''}
                                onChange={(e) => addAddressDetails(e.target.value, 'streetNo')}
                            />
                            <TextField
                                className="textField bio-field-lg"
                                label="Street name"
                                variant="outlined"
                                disabled={!canEditAddressDetails}
                                value={formData.streetName || ''}
                                onChange={(e) => addAddressDetails(e.target.value, 'streetName')}
                            />
                        </Box>
                        <Box className="bio-address-row">
                            <TextField
                                className="textField"
                                label="Suburb"
                                variant="outlined"
                                disabled={!canEditAddressDetails}
                                value={formData.suburb || ''}
                                onChange={(e) => addAddressDetails(e.target.value, 'suburb')}
                            />
                            <TextField
                                className="textField"
                                label="City"
                                variant="outlined"
                                disabled={!canEditAddressDetails}
                                value={formData.city || ''}
                                onChange={(e) => addAddressDetails(e.target.value, 'city')}
                            />
                        </Box>
                        <TextField
                            className="textField"
                            label="Country"
                            variant="outlined"
                            disabled={!canEditAddressDetails}
                            value={formData.country || ''}
                            onChange={(e) => addAddressDetails(e.target.value, 'country')}
                        />
                        {(
                            <>
                                {selectedRole!='ROLE_CMANAGER' ? <><Typography variant="body2" className="labelText" sx={{ mt: 1 }}>Your manager's email (required for employees):</Typography>
                                <TextField
                                    className="textField"
                                    label="Owner / Manager's email"
                                    variant="outlined"
                                    value={formData.reportingManagerId || ''}
                                    onChange={(e) => setFormData({ ...formData, reportingManagerId: e.target.value })}
                                />
                                </>:
                                 <>
                                <TextField
                                    className="textField"
                                    label="Owner / Manager's email"
                                    variant="outlined"
                                    value={formData.email || ''}
                                    disabled
                                    hidden={true}
                                    
                                />
                                </>}
                            </>
                            
                        )}
                        {saved && (
                            <Typography variant="body2" className="bio-saved-msg">✓ Your changes have been saved!</Typography>
                        )}
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box className="bio-container">
            <Stepper activeStep={activeStep} alternativeLabel className="bio-stepper">
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box className="bio-content">
                {renderStep()}
            </Box>

            <Box className="bio-nav">
                {activeStep > 0 && (
                    <Button variant="outlined" onClick={() => setActiveStep((s) => s - 1)}>
                        Back
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {activeStep < STEPS.length - 1 ? (
                    <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>
                        Next
                    </Button>
                ) : (
                    <Button variant="contained" onClick={save} disabled={loading}>
                        {loading ? 'Saving…' : 'Save & Finish'}
                    </Button>
                )}
            </Box>
        </Box>
    );
}